/**
 * WP-50 — the working copy the grid edits (plan 8.2: "The grid holds the
 * working copy; the base version is never modified", R32).
 *
 * The payload arrives as plan 5.5 cell rows; this module keeps them in a Map
 * keyed by "row:col" so a cell lookup during a scroll is O(1), and hands the
 * engine the array shape it documents. Every operation returns NEW sheet
 * objects — the grid never mutates a cell it was handed, so an undo can
 * restore a snapshot by reference.
 */
import { formatCellRef, parseCellRef } from './engineBridge';
import { shiftFormula, ShiftSpec } from './refShift';
import { GridCell, GridSheet, UnshiftedFormula } from './types';

export interface WorkingSheet {
  name: string;
  ordinal: number;
  hidden: boolean;
  frozenRows: number;
  frozenCols: number;
  cells: Map<string, GridCell>;
  /** Extent of the data, 1-based and inclusive. */
  maxRow: number;
  maxCol: number;
  columnWidths: Record<number, number>;
  /** Cells a person changed in THIS session — they paint as changed (plan 8.1). */
  edited: Set<string>;
}

export interface ShiftRecord {
  sheet: string;
  /** Where the cell sits AFTER the insertion — the address WP-51 writes to. */
  cell_ref: string;
  /** Where it sat before, so its previous value can still be read. */
  old_ref: string;
  old_formula: string;
  new_formula: string;
}

export class GridModelError extends Error {
  readonly location: string;

  constructor(message: string, location: string) {
    super(message);
    this.name = 'GridModelError';
    this.location = location;
  }
}

export function keyOf(row: number, col: number): string {
  return String(row) + ':' + String(col);
}

/** The cell's position, from row/col or from its A1 ref. Loud when it has neither (G13). */
function positionOf(cell: GridCell, sheetName: string, index: number): { row: number; col: number } {
  if (typeof cell.row === 'number' && typeof cell.col === 'number' && cell.row >= 1 && cell.col >= 1) {
    return { row: cell.row, col: cell.col };
  }
  if (typeof cell.ref === 'string') {
    const parsed = parseCellRef(cell.ref);
    if (parsed) return { row: parsed.row, col: parsed.col };
  }
  throw new GridModelError(
    'cell ' + String(index) + ' of sheet ' + JSON.stringify(sheetName) + ' has neither row/col nor a parseable ref',
    sheetName + ' cell index ' + String(index),
  );
}

export function toWorking(sheets: GridSheet[]): WorkingSheet[] {
  if (!Array.isArray(sheets)) {
    throw new GridModelError('the grid needs an array of sheets', 'sheets');
  }
  return sheets.map((sheet, sheetIndex) => {
    if (!sheet || typeof sheet.name !== 'string' || !Array.isArray(sheet.cells)) {
      throw new GridModelError('sheet ' + String(sheetIndex) + ' must carry a name and a cells array', 'sheets[' + String(sheetIndex) + ']');
    }
    const cells = new Map<string, GridCell>();
    let maxRow = 1;
    let maxCol = 1;
    sheet.cells.forEach((cell, i) => {
      const pos = positionOf(cell, sheet.name, i);
      const normalised: GridCell = Object.assign({}, cell, {
        row: pos.row,
        col: pos.col,
        ref: formatCellRef(pos.row, pos.col),
      });
      cells.set(keyOf(pos.row, pos.col), normalised);
      if (pos.row > maxRow) maxRow = pos.row;
      if (pos.col > maxCol) maxCol = pos.col;
    });
    return {
      name: sheet.name,
      ordinal: typeof sheet.ordinal === 'number' ? sheet.ordinal : sheetIndex,
      hidden: sheet.hidden === true,
      frozenRows: typeof sheet.frozenRows === 'number' ? Math.max(0, sheet.frozenRows) : 1,
      frozenCols: typeof sheet.frozenCols === 'number' ? Math.max(0, sheet.frozenCols) : 1,
      cells: cells,
      maxRow: Math.max(maxRow, typeof sheet.rowCount === 'number' ? sheet.rowCount : 1),
      maxCol: Math.max(maxCol, typeof sheet.colCount === 'number' ? sheet.colCount : 1),
      columnWidths: sheet.columnWidths ? Object.assign({}, sheet.columnWidths) : {},
      edited: new Set<string>(),
    };
  });
}

/** The plan 5.5 shape again, for WP-51 to persist (plan 8.2). */
export function toGridSheets(sheets: WorkingSheet[]): GridSheet[] {
  return sheets.map((sheet) => ({
    name: sheet.name,
    ordinal: sheet.ordinal,
    hidden: sheet.hidden,
    frozenRows: sheet.frozenRows,
    frozenCols: sheet.frozenCols,
    rowCount: sheet.maxRow,
    colCount: sheet.maxCol,
    columnWidths: Object.assign({}, sheet.columnWidths),
    cells: Array.from(sheet.cells.values()),
  }));
}

/** What createWorkbookContext wants: [{ name, cells }]. */
export function toEngineSheets(sheets: WorkingSheet[]): Array<{ name: string; cells: unknown[] }> {
  return sheets.map((sheet) => ({ name: sheet.name, cells: Array.from(sheet.cells.values()) }));
}

export function findSheet(sheets: WorkingSheet[], name: string): WorkingSheet | null {
  const wanted = String(name).trim().toUpperCase();
  for (let i = 0; i < sheets.length; i += 1) {
    if (String(sheets[i].name).trim().toUpperCase() === wanted) return sheets[i];
  }
  return null;
}

export function cellAt(sheet: WorkingSheet, row: number, col: number): GridCell | null {
  const cell = sheet.cells.get(keyOf(row, col));
  return cell === undefined ? null : cell;
}

/**
 * What a person typed, turned into a plan 5.5 cell. A leading '=' makes it a
 * formula (plan 8.1). Numbers accept the spreadsheet spellings — thousands
 * separators, a leading '$', a trailing '%' — and the two currency/percent
 * spellings set a number format ONLY on a cell that had none, so the
 * sponsor's own format is never overwritten.
 */
export function parseEntry(text: string, previous: GridCell | null): GridCell {
  const base: GridCell = Object.assign({}, previous || {});
  base.formula = null;
  base.shared_master_ref = null;
  base.cached_text = null;
  base.computed_value = null;
  base.compute_status = 'not_formula';
  base.raw_text = null;
  base.number_value = null;
  base.label = 'derived';

  const entry = text;
  if (entry.length === 0) {
    base.value_type = 'blank';
    return base;
  }
  if (entry.charAt(0) === '=') {
    base.formula = entry;
    base.value_type = null;
    base.compute_status = 'ok';
    return base;
  }

  const trimmed = entry.trim();
  const percent = /%$/.test(trimmed);
  const numericPart = trimmed.replace(/^\$/, '').replace(/%$/, '').replace(/,/g, '');
  const isNumber = /^-?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)$/.test(numericPart);
  if (isNumber) {
    const n = parseFloat(numericPart);
    base.value_type = 'n';
    base.number_value = percent ? n / 100 : n;
    base.raw_text = trimmed;
    if (!base.numfmt) {
      if (percent) base.numfmt = '0%';
      else if (/^\$/.test(trimmed)) base.numfmt = '$#,##0.00';
    }
    return base;
  }

  base.value_type = 's';
  base.raw_text = entry;
  return base;
}

function cloneSheet(sheet: WorkingSheet): WorkingSheet {
  return {
    name: sheet.name,
    ordinal: sheet.ordinal,
    hidden: sheet.hidden,
    frozenRows: sheet.frozenRows,
    frozenCols: sheet.frozenCols,
    cells: new Map(sheet.cells),
    maxRow: sheet.maxRow,
    maxCol: sheet.maxCol,
    columnWidths: Object.assign({}, sheet.columnWidths),
    edited: new Set(sheet.edited),
  };
}

export function snapshot(sheets: WorkingSheet[]): WorkingSheet[] {
  return sheets.map(cloneSheet);
}

/** Write one cell. Returns the new sheet list and the cell as it now stands. */
export function writeCell(
  sheets: WorkingSheet[],
  sheetName: string,
  row: number,
  col: number,
  next: GridCell,
  markEdited: boolean,
): { sheets: WorkingSheet[]; cell: GridCell } {
  const target = findSheet(sheets, sheetName);
  if (!target) throw new GridModelError('no sheet named ' + JSON.stringify(sheetName), sheetName);
  const cell: GridCell = Object.assign({}, next, {
    row: row,
    col: col,
    ref: formatCellRef(row, col),
  });
  const out = sheets.map((sheet) => {
    if (sheet !== target) return sheet;
    const copy = cloneSheet(sheet);
    copy.cells.set(keyOf(row, col), cell);
    if (markEdited) copy.edited.add(keyOf(row, col));
    if (row > copy.maxRow) copy.maxRow = row;
    if (col > copy.maxCol) copy.maxCol = col;
    return copy;
  });
  return { sheets: out, cell: cell };
}

export interface StructuralResult {
  sheets: WorkingSheet[];
  shifts: ShiftRecord[];
  unshifted: UnshiftedFormula[];
}

function applyStructural(sheets: WorkingSheet[], spec: ShiftSpec): StructuralResult {
  const target = findSheet(sheets, spec.targetSheet);
  if (!target) throw new GridModelError('no sheet named ' + JSON.stringify(spec.targetSheet), spec.targetSheet);

  const shifts: ShiftRecord[] = [];
  const unshifted: UnshiftedFormula[] = [];

  const out = sheets.map((sheet) => {
    const copy = cloneSheet(sheet);
    const moving = sheet === target;
    const nextCells = new Map<string, GridCell>();
    const nextEdited = new Set<string>();
    copy.cells.forEach((cell, key) => {
      const row = cell.row as number;
      const col = cell.col as number;
      const newRow = moving && spec.axis === 'row' && row >= spec.at ? row + spec.count : row;
      const newCol = moving && spec.axis === 'col' && col >= spec.at ? col + spec.count : col;
      let next: GridCell = cell;
      if (newRow !== row || newCol !== col) {
        next = Object.assign({}, cell, { row: newRow, col: newCol, ref: formatCellRef(newRow, newCol) });
      }
      if (typeof next.formula === 'string' && next.formula.length > 0) {
        const before = next.formula;
        const result = shiftFormula(before, sheet.name, spec);
        if (result.status === 'changed') {
          shifts.push({
            sheet: sheet.name,
            cell_ref: formatCellRef(newRow, newCol),
            old_ref: formatCellRef(row, col),
            old_formula: before,
            new_formula: result.formula,
          });
          next = Object.assign({}, next, { formula: result.formula });
        }
        if (result.reason) {
          unshifted.push({
            sheet: sheet.name,
            cell_ref: formatCellRef(newRow, newCol),
            formula: before,
            reason: result.reason,
          });
        }
      }
      nextCells.set(keyOf(newRow, newCol), next);
      if (copy.edited.has(key)) nextEdited.add(keyOf(newRow, newCol));
    });
    copy.cells = nextCells;
    copy.edited = nextEdited;
    if (moving) {
      if (spec.axis === 'col') {
        copy.maxCol += spec.count;
        const widths: Record<number, number> = {};
        Object.keys(copy.columnWidths).forEach((k) => {
          const c = parseInt(k, 10);
          widths[c >= spec.at ? c + spec.count : c] = copy.columnWidths[c];
        });
        copy.columnWidths = widths;
      } else {
        copy.maxRow += spec.count;
      }
    }
    return copy;
  });

  return { sheets: out, shifts: shifts, unshifted: unshifted };
}

export function insertColumns(
  sheets: WorkingSheet[],
  sheetName: string,
  atCol: number,
  count: number,
): StructuralResult {
  return applyStructural(sheets, { targetSheet: sheetName, axis: 'col', at: atCol, count: count });
}

export function insertRows(
  sheets: WorkingSheet[],
  sheetName: string,
  atRow: number,
  count: number,
): StructuralResult {
  return applyStructural(sheets, { targetSheet: sheetName, axis: 'row', at: atRow, count: count });
}

/** The row a column heading belongs in: the last frozen header row, or row 1. */
export function headerRowOf(sheet: WorkingSheet): number {
  return sheet.frozenRows >= 1 ? sheet.frozenRows : 1;
}
